import React from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { IrlEventTrackingEvent } from 'src/gql/graphql';
import type { IrlEvent } from 'src/gql/graphql';
import { useIrlEventFlow } from './hooks/use-irl-event-flow';
import { IrlEventSignupStart } from './components/irl-event-signup-start';
import { IrlEventEmailSignup } from './components/irl-event-email-signup';
import { IrlEventEmailLogin } from './components/irl-event-email-login';
import { IrlEventForgotPassword } from './components/irl-event-forgot-password';
import { IrlEventLoyaltyEnroll } from './components/irl-event-loyalty-enroll';
import { IrlEventSignupEnd } from './components/irl-event-signup-end';
import { IrlEventLoading } from './components/irl-event-loading';
import { IrlEventError } from './components/irl-event-error';
import { IrlEventClosed } from './components/irl-event-closed';
import { IrlEventNotStarted } from './components/irl-event-not-started';
import { FLOW_STEPS } from './types';
import { IRL_EVENT_ACTIVE_ONLY_AVAILABILITY_FLAG } from './flags';
import { useSocialAuthTracking } from './hooks/use-social-auth-tracking';

export { IRL_EVENT_ACTIVE_ONLY_AVAILABILITY_FLAG } from './flags';

export function isIrlEventNotStarted(irlEvent: IrlEvent): boolean {
  if (irlEvent.isOpen !== false || !irlEvent.startDateUtc) {
    return false;
  }

  return new Date(irlEvent.startDateUtc).getTime() > Date.now();
}

/**
 * IrlEventSignup - Main orchestrator component for IRL Event signup flow
 * - Step 1 (EVENT_SIGNUP_START): Event info + sign up options
 * - Step 2 (EVENT_EMAIL_SIGNUP): Email signup form
 * - Step 2b (EVENT_EMAIL_LOGIN): Email login form
 * - Step 2c (EVENT_EMAIL_RESET): Password reset form
 * - Step 3 (EVENT_LOYALTY_ENROLL): Loyalty enrollment step
 * - Step 4 (EVENT_SIGNUP_END): Final confirmation/success step
 */
export function IrlEventSignup(): JSX.Element {
  const flags = useFlags();
  const {
    currentStep,
    shopNowUrl,
    hasLoyaltyProgram,
    handleAuthSuccess,
    irlEvent,
    isEligibleForReward,
    isEventClosed,
    isLoading,
    loyaltyUserType,
    optInRequired,
    goToNext,
    goBack,
    setDidOptInToLoyalty,
  } = useIrlEventFlow();

  useSocialAuthTracking({ handleAuthSuccess });

  const showNotStarted =
    flags[IRL_EVENT_ACTIVE_ONLY_AVAILABILITY_FLAG] === true && !!irlEvent && isIrlEventNotStarted(irlEvent);

  if (isLoading) {
    return <IrlEventLoading />;
  }

  if (!irlEvent) {
    return <IrlEventError />;
  }

  if (showNotStarted) {
    return <IrlEventNotStarted shopNowUrl={shopNowUrl} />;
  }

  if (isEventClosed) {
    return <IrlEventClosed shopNowUrl={shopNowUrl} />;
  }

  switch (currentStep) {
    case FLOW_STEPS.EVENT_EMAIL_SIGNUP:
      return (
        <IrlEventEmailSignup
          irlEvent={irlEvent}
          onSignIn={() => goToNext(FLOW_STEPS.EVENT_EMAIL_LOGIN)}
          onSignupSuccess={() => void handleAuthSuccess({ authEvent: IrlEventTrackingEvent.signup })}
          onBack={goBack}
        />
      );

    case FLOW_STEPS.EVENT_EMAIL_LOGIN:
      return (
        <IrlEventEmailLogin
          irlEvent={irlEvent}
          onLoginSuccess={() => void handleAuthSuccess({ authEvent: IrlEventTrackingEvent.login })}
          onBack={goBack}
          onForgotPassword={() => goToNext(FLOW_STEPS.EVENT_EMAIL_RESET)}
        />
      );

    case FLOW_STEPS.EVENT_EMAIL_RESET:
      return <IrlEventForgotPassword irlEvent={irlEvent} onBack={goBack} onResetSuccess={() => goToNext()} />;

    case FLOW_STEPS.EVENT_LOYALTY_ENROLL:
      return (
        <IrlEventLoyaltyEnroll
          irlEvent={irlEvent}
          onEnroll={() => {
            setDidOptInToLoyalty(true);
            goToNext();
          }}
          onSkip={() => {
            setDidOptInToLoyalty(false);
            goToNext();
          }}
        />
      );

    case FLOW_STEPS.EVENT_SIGNUP_END:
      return (
        <IrlEventSignupEnd
          hasIncentive={irlEvent.hasIncentive}
          hasLoyaltyProgram={hasLoyaltyProgram}
          isEligibleForReward={isEligibleForReward}
          loyaltyUserType={loyaltyUserType}
          optInRequired={optInRequired}
          shopNowUrl={shopNowUrl}
        />
      );

    case FLOW_STEPS.EVENT_SIGNUP_START:
    default:
      return <IrlEventSignupStart irlEvent={irlEvent} onContinueWithEmail={() => goToNext()} />;
  }
}
